import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Button, DatePicker, Input, Select } from 'antd'
import { DATE_FORMAT, QUERY_DATE_FORMAT, getDefaultFilters, toPartnerIds } from '../../helpers/history'
import removeVietnameseTones from '../../../../helpers/removeVietnameseTones'

const { RangePicker } = DatePicker
const { Option } = Select

const _toRange = ({ from, to }) => (from && to) ? [moment(from, QUERY_DATE_FORMAT), moment(to, QUERY_DATE_FORMAT)] : []

// Tìm đối tác may không phân biệt hoa thường / dấu, khớp cả tên lẫn mã (cùng cách SelectPartnerSewing).
const _filterPartnerOption = (input, option) => {
    const keyword = removeVietnameseTones(input).trim()
    return removeVietnameseTones(option.props.searchText).indexOf(keyword) >= 0
}

// Ô Tên lô gõ xong mới lọc, tránh gọi API theo từng ký tự.
const BATCH_NAME_DEBOUNCE_MS = 500

const HistoryFilters = ({ filters, partners, handedByList, loading, onSearch, onReset }) => {
    // Bộ lọc tự áp dụng khi đổi giá trị; draft giữ chữ đang gõ ở ô Tên lô.
    const [draft, setDraft] = useState(filters)
    const debounceRef = useRef(null)

    useEffect(() => {
        setDraft(filters)
    }, [filters])

    useEffect(() => () => clearTimeout(debounceRef.current), [])

    const _apply = (next) => {
        clearTimeout(debounceRef.current)
        setDraft(next)
        onSearch({ ...next, batch_name: `${next.batch_name || ''}`.trim().toUpperCase() })
    }

    const _onChange = (key, value) => _apply({ ...draft, [key]: value })

    const _onChangeRange = (range) => {
        const [from, to] = range || []
        _apply({
            ...draft,
            from: from ? from.format(QUERY_DATE_FORMAT) : '',
            to: to ? to.format(QUERY_DATE_FORMAT) : '',
        })
    }

    const _onChangeBatchName = (e) => {
        const next = { ...draft, batch_name: e.target.value }
        setDraft(next)
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => _apply(next), BATCH_NAME_DEBOUNCE_MS)
    }

    // Reset draft ngay: nếu URL đã là bộ lọc mặc định thì filters không đổi và effect ở trên không chạy.
    const _onReset = () => {
        clearTimeout(debounceRef.current)
        setDraft(getDefaultFilters())
        onReset()
    }

    // Enter ở ô Tên lô lọc ngay, không chờ debounce.
    const _onPressEnter = () => _apply(draft)

    return (
        <form className='HistoryPanel HistoryFilters' onSubmit={e => e.preventDefault()}>
            <div className='HistoryFilters__field'>
                <label>Ngày giao</label>
                <RangePicker
                    format={DATE_FORMAT}
                    value={_toRange(draft)}
                    onChange={_onChangeRange}
                    style={{ width: 240 }}
                />
            </div>

            <div className='HistoryFilters__field'>
                <label>Người giao</label>
                <Select
                    showSearch
                    value={draft.handed_by || ''}
                    onChange={value => _onChange('handed_by', value)}
                    style={{ width: 240 }}
                >
                    <Option value=''>Tất cả</Option>
                    {handedByList.map(username => (
                        <Option key={username} value={username}>{username}</Option>
                    ))}
                </Select>
            </div>

            <div className='HistoryFilters__field'>
                <label>Tên lô</label>
                <Input
                    allowClear
                    placeholder='VD: R-46906'
                    value={draft.batch_name}
                    onChange={_onChangeBatchName}
                    onPressEnter={_onPressEnter}
                    style={{ width: 240 }}
                />
            </div>

            {/* Đặt cuối, cạnh Xoá lọc: ô rộng hơn để chọn nhiều đối tác, cao quá 300px thì cuộn (history.scss) */}
            <div className='HistoryFilters__field'>
                <label>Đối tác may</label>
                {/* Chọn nhiều; để trống = tất cả đối tác */}
                <Select
                    className='HistoryFilters__partner'
                    mode='multiple'
                    allowClear
                    showSearch
                    filterOption={_filterPartnerOption}
                    placeholder='Tất cả'
                    value={toPartnerIds(draft.partner)}
                    onChange={values => _onChange('partner', values.join(','))}
                    style={{ width: 480 }}
                    // Danh sách cao tối đa 500px (mặc định antd 250px) để thấy nhiều đối tác hơn
                    dropdownMenuStyle={{ maxHeight: 500 }}
                >
                    {partners.map(partner => (
                        <Option key={partner._id} value={partner._id} searchText={`${partner.name} ${partner.code || ''}`}>
                            {partner.name}
                        </Option>
                    ))}
                </Select>
            </div>

            <div className='HistoryFilters__actions'>
                <Button onClick={_onReset} disabled={loading}>Xoá lọc</Button>
            </div>
        </form>
    )
}

HistoryFilters.propTypes = {
    filters: PropTypes.object.isRequired,
    partners: PropTypes.array.isRequired,
    handedByList: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    onSearch: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
}

export default HistoryFilters
